import React, { useState, useEffect, useMemo } from 'react';
import {
   Box,
   Typography,
   Button,
   Paper,
   CircularProgress,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   ListItemSecondaryAction,
   Avatar,
   IconButton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogContentText,
   DialogActions,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Divider,
   Alert,
   Tooltip,
   Card,
   CardContent,
   Snackbar,
   Tabs,
   Tab,
   InputAdornment,
   Chip,
   Pagination,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   Delete as DeleteIcon,
   Edit as EditIcon,
   PersonRemove as PersonRemoveIcon,
   Email as EmailIcon,
   People as PeopleIcon,
   Send as SendIcon,
   Refresh as RefreshIcon,
   ContentCopy as CopyIcon,
   Phone as PhoneIcon,
   HourglassEmpty,
   Contacts as ContactsIcon,
   Check as CheckIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { Trip, TripParticipant, TripRole } from '../../types/Trip';
import {
   getTripParticipants,
   inviteUserToTrip,
   removeUserFromTrip,
   changeParticipantRole,
   resendInvitation,
} from '../../services/tripParticipantService';
import {
   doc,
   getDoc,
   query,
   collection,
   getDocs,
   where,
   deleteDoc,
   updateDoc,
   addDoc,
   serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import SMSInviteForm from '../SMSInviteForm';
import { useNavigate } from 'react-router-dom';

interface TripParticipantsManagerProps {
   trip: Trip;
   onParticipantsUpdated: () => void;
   userRole?: string; // Add userRole prop to know current user's role
   onAddToContacts?: (participant: TripParticipant) => void; // Callback to add participant to contacts
   addingContact?: string; // ID of participant being added to contacts
   addedContacts?: Set<string>; // Set of participant IDs already added to contacts
   onOpenParticipantProfile?: (participant: TripParticipant) => void; // Callback to open participant profile modal
}

const TripParticipantsManager: React.FC<TripParticipantsManagerProps> = ({
   trip,
   onParticipantsUpdated,
   userRole,
   onAddToContacts,
   addingContact = '',
   addedContacts = new Set(),
   onOpenParticipantProfile,
}) => {
   const { currentUser } = useUser();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const [participants, setParticipants] = useState<TripParticipant[]>([]);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [resendLoading, setResendLoading] = useState<string>(''); // Store email of participant we're resending to
   const [copySuccess, setCopySuccess] = useState<boolean>(false);
   const path = useNavigate();

   // Delete confirmation dialog state
   const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
   const [participantToDelete, setParticipantToDelete] = useState<TripParticipant | null>(null);

   // Edit role dialog state
   const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
   const [participantToEdit, setParticipantToEdit] = useState<TripParticipant | null>(null);
   const [newRole, setNewRole] = useState<TripRole>('viewer');

   // Pagination state
   const [currentPage, setCurrentPage] = useState<number>(1);
   const participantsPerPage = 8;

   // Check if current user can manage participants (owners and editors)
   const canManageParticipants = useMemo(() => {
      if (!currentUser || !trip) return false;

      // Trip creator always has management access
      const isCreator =
         trip.userId === currentUser.uid ||
         String(trip.userId) === String(currentUser.uid) ||
         (trip as any).createdBy === currentUser.uid;

      // Also check if user has owner or editor role
      const hasManagementRole = userRole === 'owner' || userRole === 'editor';

      return isCreator || hasManagementRole;
   }, [trip, currentUser, userRole]);

   // Calculate paginated participants
   const paginatedParticipants = useMemo(() => {
      const startIndex = (currentPage - 1) * participantsPerPage;
      const endIndex = startIndex + participantsPerPage;
      return participants.slice(startIndex, endIndex);
   }, [participants, currentPage, participantsPerPage]);

   // Calculate total pages
   const totalPages = Math.ceil(participants.length / participantsPerPage);

   // Enhanced debugging
   useEffect(() => {}, [canManageParticipants, participants.length, loading, trip, currentUser]);

   // Reset to first page when participants change
   useEffect(() => {
      setCurrentPage(1);
   }, [participants.length]);

   // Fetch participants on component mount
   useEffect(() => {
      fetchParticipants();
   }, [trip.id]);

   const fetchParticipants = async () => {
      try {
         setLoading(true);

         // Validate trip ID
         if (!trip?.id) {
            console.error('Missing trip ID');
            setError('Cannot load participants: Invalid trip data');
            setParticipants([]);
            return;
         }

         // Add additional error handling
         try {
            const fetchedParticipants = await getTripParticipants(trip.id);

            // Validate returned participants
            if (Array.isArray(fetchedParticipants)) {
               let updatedParticipants = [...fetchedParticipants];

               // Check relationship status for each participant if current user exists
               if (currentUser && updatedParticipants.length > 0) {
                  try {
                     // Get all relationships for the current user (both directions)
                     const relationshipNetworkRef = collection(db, 'relationship_network');

                     // Query 1: Where current user is the initiator
                     const q1 = query(
                        relationshipNetworkRef,
                        where('userId', '==', currentUser.uid),
                        where('status', '==', 'accepted')
                     );

                     // Query 2: Where current user is the recipient (reverse relationships)
                     const q2 = query(
                        relationshipNetworkRef,
                        where('relationToUserId', '==', currentUser.uid),
                        where('status', '==', 'accepted')
                     );

                     const [querySnapshot1, querySnapshot2] = await Promise.all([
                        getDocs(q1),
                        getDocs(q2),
                     ]);

                     const relationships1 = querySnapshot1.docs.map((doc) => doc.data());
                     const relationships2 = querySnapshot2.docs.map((doc) => doc.data());
                     const allRelationships = [...relationships1, ...relationships2];

                     // Update participants with relationship status
                     updatedParticipants = updatedParticipants.map(
                        (participant: TripParticipant) => {
                           // Check if this participant is in the current user's network (both directions)
                           const isInNetwork = allRelationships.some((rel) => {
                              // Check forward relationship (current user -> participant)
                              const forwardMatch =
                                 rel.userId === currentUser.uid &&
                                 (rel.relationToUserId === participant.userId ||
                                    rel.relationToEmail === participant.email);

                              // Check reverse relationship (participant -> current user)
                              const reverseMatch =
                                 rel.relationToUserId === currentUser.uid &&
                                 (rel.userId === participant.userId ||
                                    rel.recipientEmail === participant.email);

                              return forwardMatch || reverseMatch;
                           });

                           return {
                              ...participant,
                              isInNetwork: isInNetwork,
                           };
                        }
                     );
                  } catch (relationshipError) {
                     console.error('Error fetching relationship status:', relationshipError);
                     // Continue without relationship status if there's an error
                  }
               }

               // Fetch user profiles for participant avatars
               if (updatedParticipants.length > 0) {
                  try {
                     const userIds = updatedParticipants
                        .map((participant) => participant.userId)
                        .filter((id) => id);

                     if (userIds.length > 0) {
                        const userProfiles: { [userId: string]: any } = {};
                        
                        for (const userId of userIds) {
                           try {
                              // Try userProfiles first (where complete profile data is stored)
                              let userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));

                              if (!userProfileDoc.exists()) {
                                 // Fallback to userProfile collection
                                 userProfileDoc = await getDoc(doc(db, 'userProfile', userId));
                              }

                              if (!userProfileDoc.exists()) {
                                 // Fallback to users collection
                                 userProfileDoc = await getDoc(doc(db, 'users', userId));
                              }

                              if (userProfileDoc.exists()) {
                                 userProfiles[userId] = userProfileDoc.data();
                              }
                           } catch (error) {
                              console.error(`Error fetching profile for user ${userId}:`, error);
                           }
                        }

                        // Add profile data to participants
                        updatedParticipants = updatedParticipants.map((participant) => ({
                           ...participant,
                           profileData: userProfiles[participant.userId] || null,
                        }));
                     }
                  } catch (profileError) {
                     console.error('Error fetching user profiles:', profileError);
                     // Continue without profile data if there's an error
                  }
               }

               setParticipants(updatedParticipants);
               setError(null);
            } else {
               console.error('Invalid participants data returned:', fetchedParticipants);
               setParticipants([]);
               setError('Failed to load participants: Invalid data format');
            }
         } catch (fetchError) {
            console.error('Error fetching participants:', fetchError);
            // Show more specific error based on the error type
            if (fetchError instanceof Error) {
               setError(`Failed to load participants: ${fetchError.message}`);
            } else {
               setError('Failed to load participants. Please try again.');
            }
            setParticipants([]);
         }
      } catch (err) {
         console.error('Error in fetchParticipants:', err);
         setError('Failed to load participants. Please try again.');
         setParticipants([]);
      } finally {
         setLoading(false);
      }
   };

   const handleRemoveUser = (participant: TripParticipant) => {
      setParticipantToDelete(participant);
      setOpenDeleteDialog(true);
   };

   const confirmRemoveUser = async () => {
      if (!participantToDelete || !participantToDelete.userId || !trip.id) return;

      try {
         setLoading(true);
         await removeUserFromTrip(trip.id, participantToDelete.userId);

         // Refresh the participants list
         fetchParticipants();
         onParticipantsUpdated();

         setSuccess(
            `${participantToDelete.displayName || participantToDelete.email} removed from trip`
         );
      } catch (err) {
         console.error('Error removing user:', err);
         setError('Failed to remove user. Please try again.');
      } finally {
         setLoading(false);
         setOpenDeleteDialog(false);
         setParticipantToDelete(null);
      }
   };

   const handleEditRole = (participant: TripParticipant) => {
      setParticipantToEdit(participant);
      setNewRole(participant.role);
      setOpenEditDialog(true);
   };

   const confirmEditRole = async () => {
      if (!participantToEdit || !participantToEdit.userId || !trip.id) return;

      try {
         setLoading(true);
         await changeParticipantRole(trip.id, participantToEdit.userId, newRole);

         // Refresh the participants list
         fetchParticipants();
         onParticipantsUpdated();

         setSuccess(
            `${participantToEdit.displayName || participantToEdit.email}'s role updated to ${newRole}`
         );
      } catch (err) {
         console.error('Error updating role:', err);
         setError('Failed to update role. Please try again.');
      } finally {
         setLoading(false);
         setOpenEditDialog(false);
         setParticipantToEdit(null);
      }
   };

   const handleResendInvite = async (participant: TripParticipant) => {
      if (!participant.email || !currentUser?.uid || !trip.id) return;

      try {
         setResendLoading(participant.email);

         await resendInvitation(trip.id);

         setSuccess(`Invitation resent to ${participant.email}`);

         // Refresh participants list to update invite timestamp
         fetchParticipants();
         onParticipantsUpdated();
      } catch (err) {
         console.error('Error resending invitation:', err);
         setError('Failed to resend invitation. Please try again.');
      } finally {
         setResendLoading('');
      }
   };

   const handleCopyInviteLink = async (participant: TripParticipant) => {
      try {
         // Check if there's an existing invitation
         const invitationsQuery = query(
            collection(db, 'tripInvitations'),
            where('tripId', '==', trip.id),
            where('email', '==', participant.email.toLowerCase()),
            where('status', '==', 'pending')
         );

         const invitationsSnapshot = await getDocs(invitationsQuery);

         if (invitationsSnapshot.empty) {
            setError('No active invitation found. Please resend the invitation first.');
            return;
         }

         const invitationId = invitationsSnapshot.docs[0].id;
         const inviteUrl = `${window.location.origin}/invite?id=${invitationId}`;

         await navigator.clipboard.writeText(inviteUrl);
         setCopySuccess(true);

         // Hide success message after 3 seconds
         setTimeout(() => {
            setCopySuccess(false);
         }, 3000);
      } catch (err) {
         console.error('Error copying invite link:', err);
         setError('Failed to copy invitation link. Please try again.');
      }
   };

   // Clear status messages after 5 seconds
   useEffect(() => {
      if (success) {
         const timer = setTimeout(() => setSuccess(null), 5000);
         return () => clearTimeout(timer);
      }
      if (error) {
         const timer = setTimeout(() => setError(null), 5000);
         return () => clearTimeout(timer);
      }
   }, [success, error]);

   // Handle deleting a participant
   const handleDeleteParticipant = async () => {
      if (!participantToDelete || !participantToDelete.userId) return;

      if (!trip.id) {
         setError('Trip ID is missing');
         return;
      }

      try {
         setLoading(true);
         await removeUserFromTrip(trip.id, participantToDelete.userId);

         // Refresh the participants list
         fetchParticipants();

         setSuccess(
            `${participantToDelete.displayName || participantToDelete.email} removed from trip`
         );
         setOpenDeleteDialog(false);
         setParticipantToDelete(null);
      } catch (err: any) {
         console.error('Error deleting participant:', err);
         setError(err.message || 'Failed to remove participant');
      } finally {
         setLoading(false);
      }
   };

   // Handle updating a participant's role
   const handleUpdateRole = async () => {
      if (!participantToEdit || !participantToEdit.userId) return;

      if (!trip.id) {
         setError('Trip ID is missing');
         return;
      }

      try {
         setLoading(true);
         await changeParticipantRole(trip.id, participantToEdit.userId, newRole);

         // Refresh the participants list
         fetchParticipants();

         setSuccess(
            `Updated ${participantToEdit.displayName || participantToEdit.email}'s role to ${newRole}`
         );
         setOpenEditDialog(false);
         setParticipantToEdit(null);
      } catch (err: any) {
         console.error('Error updating participant role:', err);
         setError(err.message || 'Failed to update role');
      } finally {
         setLoading(false);
      }
   };

   return (
      <Box>
         {/* Debug information to troubleshoot owner check */}
         <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, display: 'none' }}>
            <Typography variant='caption'>
               Debug: currentUser ID: {currentUser?.uid || 'none'}, Trip owner ID:{' '}
               {trip?.userId || 'none'}, canManage: {canManageParticipants ? 'true' : 'false'}
            </Typography>
         </Box>

         {/* Status messages */}
         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}
         {success && (
            <Alert severity='success' sx={{ mb: 2 }}>
               {success}
            </Alert>
         )}

         {/* Loading indicator */}
         {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
               <CircularProgress />
            </Box>
         )}

         {/* Empty state */}
         {!loading && participants.length === 0 && (
            <Card sx={{ mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
               <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                     <PeopleIcon
                        sx={{ fontSize: 60, color: 'primary.main', mb: 2, opacity: 0.7 }}
                     />
                     <Typography variant='h6' gutterBottom>
                        No participants yet
                     </Typography>
                     <Typography variant='body1' color='text.secondary'>
                        This trip doesn't have any participants at the moment
                     </Typography>
                  </Box>
               </CardContent>
            </Card>
         )}

         {/* Snackbar for copy success */}
         <Snackbar
            open={copySuccess}
            autoHideDuration={3000}
            onClose={() => setCopySuccess(false)}
            message='Invitation link copied to clipboard'
         />

         {/* Participants list */}
         {!loading && participants.length > 0 && (
            <Box>
               <Paper
                  sx={{
                     mb: isMobile ? 0 : 3,
                     borderRadius: isMobile ? 0 : 1,
                     boxShadow: isMobile ? 'none' : 1,
                  }}
               >
                  {/* Participants Header */}
                  <Box
                     sx={{
                        p: isMobile ? 0 : 2,
                        borderBottom: isMobile ? 'none' : '1px solid',
                        borderColor: 'divider',
                     }}
                  >
                     <Typography
                        variant={isMobile ? 'h6' : 'h6'}
                        sx={{
                           fontWeight: isMobile ? 600 : 600,
                           fontSize: isMobile ? '1.125rem' : '1.25rem',
                           mb: isMobile ? 1 : 0,
                        }}
                     >
                        Trip Participants ({participants.length})
                     </Typography>
                     {totalPages > 1 && (
                        <Typography variant='body2' color='text.secondary'>
                           Showing {(currentPage - 1) * participantsPerPage + 1}-
                           {Math.min(currentPage * participantsPerPage, participants.length)} of{' '}
                           {participants.length}
                        </Typography>
                     )}
                  </Box>

                  {/* Check for pending invitations */}
                  {/* {participants.some((p) => p.status === 'pending') && (
                     <Box sx={{ bgcolor: 'info.light', p: 2 }}>
                        <Typography variant='subtitle1' fontWeight='bold' color='info.contrastText'>
                           Pending Invitations
                        </Typography>
                        <Typography variant='body2' color='info.contrastText'>
                           These invitations have been sent but not yet accepted
                        </Typography>
                     </Box>
                  )} */}

                  <List>
                     {/* First show pending invitations */}
                     {/* {participants
                        .filter((p) => p.status === 'pending')
                        .map((participant) => (
                           <ListItem
                              key={participant.userId || participant.email}
                              divider
                              sx={{ bgcolor: 'action.hover' }}
                           >
                              <ListItemAvatar>
                                 <Avatar src={participant.photoURL || undefined}>
                                    {participant.displayName?.charAt(0) ||
                                       participant.email?.charAt(0)}
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                       <Typography variant='body1'>
                                          {participant.displayName ||
                                             participant.email.split('@')[0]}
                                       </Typography>
                                       <Chip
                                          label='Invitation Pending'
                                          size='small'
                                          color='warning'
                                          icon={<HourglassEmpty fontSize='small' />}
                                          sx={{ ml: 1 }}
                                       />
                                    </Box>
                                 }
                                 secondary={
                                    <>
                                       {participant.email}
                                       <Chip
                                          label='Editor'
                                          size='small'
                                          variant='outlined'
                                          sx={{ ml: 1, fontSize: '0.7rem' }}
                                       />
                                       <Typography
                                          variant='caption'
                                          display='block'
                                          color='text.secondary'
                                          sx={{ mt: 0.5 }}
                                       >
                                          Invited{' '}
                                          {participant.invitedAt
                                             ? new Date(participant.invitedAt).toLocaleDateString()
                                             : 'recently'}
                                       </Typography>
                                    </>
                                 }
                              />

                              {canManageParticipants && (
                                 <ListItemSecondaryAction>
                                    <Tooltip title='Resend invitation'>
                                       <IconButton
                                          edge='end'
                                          aria-label='resend'
                                          onClick={() => handleResendInvite(participant)}
                                          disabled={resendLoading === participant.email}
                                          sx={{ mr: 1 }}
                                       >
                                          {resendLoading === participant.email ? (
                                             <CircularProgress size={20} />
                                          ) : (
                                             <SendIcon color='primary' />
                                          )}
                                       </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Delete invitation'>
                                       <IconButton
                                          edge='end'
                                          aria-label='delete'
                                          onClick={() => handleRemoveUser(participant)}
                                       >
                                          <DeleteIcon color='error' />
                                       </IconButton>
                                    </Tooltip>
                                 </ListItemSecondaryAction>
                              )}
                           </ListItem>
                        ))} */}

                     {/* Show paginated participants */}
                     {paginatedParticipants
                        .filter((p) => p.status === 'accepted' && p.userId === trip.userId)
                        .map((participant) => (
                           <ListItem
                              key={participant.userId || participant.email}
                              divider={isMobile}
                              onClick={() => onOpenParticipantProfile?.(participant)}
                              sx={{
                                 cursor: 'pointer',
                                 py: isMobile ? 2 : 1,
                                 px: isMobile ? 0 : 2,
                                 position: 'relative',
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'stretch',
                                 '&:hover': {
                                    backgroundColor: isMobile ? 'rgba(0,0,0,0.04)' : 'action.hover',
                                 },
                                 borderRadius: isMobile ? 0 : 1,
                              }}
                           >
                              <ListItemAvatar>
                                 <Avatar
                                    src={
                                       participant.profileData?.profilePicture ||
                                       participant.profileData?.photoURL ||
                                       participant.profileData?.profilePhoto ||
                                       participant.profileData?.avatar ||
                                       participant.photoURL ||
                                       undefined
                                    }
                                    sx={{ bgcolor: 'primary.main' }}
                                 >
                                    {participant.profileData?.displayName?.charAt(0) ||
                                       participant.profileData?.name?.charAt(0) ||
                                       participant.profileData?.fullName?.charAt(0) ||
                                       participant.displayName?.charAt(0) ||
                                       participant.email?.charAt(0)}
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                       <Typography
                                          variant='body1'
                                          sx={{
                                             fontWeight: isMobile ? 500 : 400,
                                             fontSize: isMobile ? '1rem' : '0.875rem',
                                          }}
                                       >
                                          {participant.displayName ||
                                             participant.email.split('@')[0]}
                                       </Typography>
                                       <Chip
                                          size='small'
                                          label='Owner'
                                          sx={{
                                             bgcolor: isMobile ? '#01949A' : 'primary.main',
                                             color: 'white',
                                             fontSize: '0.75rem',
                                             height: '24px',
                                             borderRadius: '12px',
                                             fontWeight: 500,
                                          }}
                                       />
                                    </Box>
                                 }
                                 secondary={
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{
                                          fontSize: isMobile ? '0.875rem' : '0.75rem',
                                          mt: isMobile ? 0.5 : 0,
                                       }}
                                    >
                                       {participant.email}
                                    </Typography>
                                 }
                              />

                              {/* Buttons for trip owner if not current user */}
                              {participant.userId !== currentUser?.uid &&
                                 onAddToContacts &&
                                 (isMobile ? (
                                    // Mobile: Button below text content
                                    <Box sx={{ mt: 1, ml: 7 }}>
                                       {participant.isInNetwork ||
                                       addedContacts.has(
                                          participant.userId || participant.email || ''
                                       ) ? (
                                          <Button
                                             variant='outlined'
                                             size='small'
                                             startIcon={<CheckIcon />}
                                             disabled
                                             sx={{
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1.5,
                                                color: 'success.main',
                                                borderColor: 'success.main',
                                                borderRadius: '20px',
                                             }}
                                          >
                                             Added
                                          </Button>
                                       ) : (
                                          <Button
                                             variant='contained'
                                             size='small'
                                             startIcon={
                                                addingContact ===
                                                (participant.userId || participant.email) ? (
                                                   <CircularProgress size={12} />
                                                ) : (
                                                   <ContactsIcon />
                                                )
                                             }
                                             onClick={() => onAddToContacts(participant)}
                                             disabled={
                                                addingContact ===
                                                (participant.userId || participant.email)
                                             }
                                             sx={{
                                                fontSize: '0.75rem',
                                                py: 0.75,
                                                px: 2,
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                '&:hover': {
                                                   bgcolor: 'primary.dark',
                                                },
                                             }}
                                          >
                                             Start the conversation
                                          </Button>
                                       )}
                                    </Box>
                                 ) : (
                                    // Desktop: Secondary action (right side)
                                    <ListItemSecondaryAction>
                                       <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                          {participant.isInNetwork ||
                                          addedContacts.has(
                                             participant.userId || participant.email || ''
                                          ) ? (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<CheckIcon />}
                                                disabled
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   color: 'success.main',
                                                   borderColor: 'success.main',
                                                }}
                                             >
                                                Added
                                             </Button>
                                          ) : (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={
                                                   addingContact ===
                                                   (participant.userId || participant.email) ? (
                                                      <CircularProgress size={12} />
                                                   ) : (
                                                      <ContactsIcon />
                                                   )
                                                }
                                                onClick={() => onAddToContacts(participant)}
                                                disabled={
                                                   addingContact ===
                                                   (participant.userId || participant.email)
                                                }
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   borderRadius: '4px',
                                                   textTransform: 'none',
                                                   fontWeight: 400,
                                                   bgcolor: 'transparent',
                                                   color: 'primary.main',
                                                   borderColor: 'primary.main',
                                                   '&:hover': {
                                                      bgcolor: 'primary.main',
                                                      color: 'white',
                                                      borderColor: 'primary.main',
                                                   },
                                                }}
                                             >
                                                Start the conversation
                                             </Button>
                                          )}
                                       </Box>
                                    </ListItemSecondaryAction>
                                 ))}
                           </ListItem>
                        ))}

                     {/* Other accepted participants */}
                     {paginatedParticipants
                        .filter((p) => p.status === 'accepted' && p.userId !== trip.userId)
                        .map((participant) => (
                           <ListItem
                              key={participant.userId || participant.email}
                              divider
                              onClick={() => onOpenParticipantProfile?.(participant)}
                              sx={{
                                 cursor: 'pointer',
                                 py: isMobile ? 2 : 1,
                                 px: isMobile ? 0 : 2,
                                 position: 'relative',
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'stretch',
                                 '&:hover': {
                                    backgroundColor: isMobile ? 'rgba(0,0,0,0.04)' : 'action.hover',
                                 },
                                 borderRadius: isMobile ? 0 : 1,
                              }}
                           >
                              <ListItemAvatar>
                                 <Avatar
                                    src={
                                       participant.profileData?.profilePicture ||
                                       participant.profileData?.photoURL ||
                                       participant.profileData?.profilePhoto ||
                                       participant.profileData?.avatar ||
                                       participant.photoURL ||
                                       undefined
                                    }
                                 >
                                    {participant.profileData?.displayName?.charAt(0) ||
                                       participant.profileData?.name?.charAt(0) ||
                                       participant.profileData?.fullName?.charAt(0) ||
                                       participant.displayName?.charAt(0) ||
                                       participant.email?.charAt(0)}
                                 </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                 primary={
                                    <Typography variant='body1'>
                                       {participant.displayName || participant.email.split('@')[0]}
                                    </Typography>
                                 }
                                 secondary={
                                    <>
                                       {participant.email}
                                       {participant.role !== 'owner' && (
                                          <Typography
                                             component='span'
                                             variant='caption'
                                             sx={{
                                                ml: 1,
                                                bgcolor: 'grey.200',
                                                px: 1,
                                                py: 0.2,
                                                borderRadius: 1,
                                             }}
                                          >
                                             {participant.role === 'traveler' ||
                                             participant.role === 'editor'
                                                ? 'Editor'
                                                : 'Viewer'}
                                          </Typography>
                                       )}
                                    </>
                                 }
                              />

                              {isMobile ? (
                                 // Mobile: Buttons below text content in row layout
                                 <Box
                                    sx={{
                                       mt: 1,
                                       ml: 0,
                                       mr: 0,
                                       px: 2,
                                       py: 1,
                                       display: 'flex',
                                       flexDirection: 'row',
                                       gap: 1,
                                       width: '100%',
                                       alignItems: 'center',
                                       flexWrap: 'wrap',
                                       backgroundColor: 'rgba(0,0,0,0.02)',
                                       borderRadius: 1,
                                       position: 'relative',
                                       zIndex: 1,
                                    }}
                                 >
                                    {participant.userId === currentUser?.uid ? (
                                       <Chip
                                          label='You'
                                          size='small'
                                          color='primary'
                                          variant='outlined'
                                          sx={{ fontWeight: 'medium' }}
                                       />
                                    ) : (
                                       <>
                                          {/* Start Conversation Button - visible to all users */}
                                          {onAddToContacts &&
                                             (participant.isInNetwork ||
                                             addedContacts.has(
                                                participant.userId || participant.email || ''
                                             ) ? (
                                                <Button
                                                   variant='outlined'
                                                   size='small'
                                                   startIcon={<CheckIcon />}
                                                   disabled
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.5,
                                                      px: 1.5,
                                                      color: 'success.main',
                                                      borderColor: 'success.main',
                                                      borderRadius: '20px',
                                                      width: 'auto',
                                                   }}
                                                >
                                                   Added
                                                </Button>
                                             ) : (
                                                <Button
                                                   variant='contained'
                                                   size='small'
                                                   startIcon={
                                                      addingContact ===
                                                      (participant.userId || participant.email) ? (
                                                         <CircularProgress size={12} />
                                                      ) : (
                                                         <ContactsIcon />
                                                      )
                                                   }
                                                   onClick={() => onAddToContacts(participant)}
                                                   disabled={
                                                      addingContact ===
                                                      (participant.userId || participant.email)
                                                   }
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.75,
                                                      px: 2,
                                                      borderRadius: '20px',
                                                      textTransform: 'none',
                                                      fontWeight: 500,
                                                      bgcolor: 'primary.main',
                                                      color: 'white',
                                                      width: 'auto',
                                                      '&:hover': {
                                                         bgcolor: 'primary.dark',
                                                      },
                                                   }}
                                                >
                                                   Start the conversation
                                                </Button>
                                             ))}

                                          {/* Management Buttons - only for editors/owners - in COLUMN */}
                                          {canManageParticipants && (
                                             <>
                                                <Button
                                                   variant='outlined'
                                                   size='small'
                                                   startIcon={<EditIcon />}
                                                   onClick={() => handleEditRole(participant)}
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.5,
                                                      px: 1.5,
                                                      borderRadius: '20px',
                                                      borderColor: 'secondary.main',
                                                      color: 'secondary.main',
                                                      width: 'auto',
                                                      '&:hover': {
                                                         bgcolor: 'secondary.main',
                                                         color: 'white',
                                                         borderColor: 'secondary.main',
                                                      },
                                                   }}
                                                >
                                                   Edit
                                                </Button>
                                                <Button
                                                   variant='outlined'
                                                   size='small'
                                                   startIcon={<DeleteIcon />}
                                                   onClick={() => handleRemoveUser(participant)}
                                                   sx={{
                                                      fontSize: '0.75rem',
                                                      py: 0.5,
                                                      px: 1.5,
                                                      borderRadius: '20px',
                                                      borderColor: 'error.main',
                                                      color: 'error.main',
                                                      width: 'auto',
                                                      '&:hover': {
                                                         bgcolor: 'error.main',
                                                         color: 'white',
                                                         borderColor: 'error.main',
                                                      },
                                                   }}
                                                >
                                                   Remove
                                                </Button>
                                             </>
                                          )}
                                       </>
                                    )}
                                 </Box>
                              ) : (
                                 // Desktop: Secondary action (right side)
                                 <ListItemSecondaryAction>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                       {/* Start Conversation Button - visible to all users */}
                                       {onAddToContacts &&
                                          (participant.isInNetwork ||
                                          addedContacts.has(
                                             participant.userId || participant.email || ''
                                          ) ? (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<CheckIcon />}
                                                disabled
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   color: 'success.main',
                                                   borderColor: 'success.main',
                                                }}
                                             >
                                                Added
                                             </Button>
                                          ) : (
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={
                                                   addingContact ===
                                                   (participant.userId || participant.email) ? (
                                                      <CircularProgress size={12} />
                                                   ) : (
                                                      <ContactsIcon />
                                                   )
                                                }
                                                onClick={() => onAddToContacts(participant)}
                                                disabled={
                                                   addingContact ===
                                                   (participant.userId || participant.email)
                                                }
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   borderColor: 'primary.main',
                                                   color: 'primary.main',
                                                   '&:hover': {
                                                      bgcolor: 'primary.main',
                                                      color: 'white',
                                                      borderColor: 'primary.main',
                                                   },
                                                }}
                                             >
                                                Start the conversation
                                             </Button>
                                          ))}

                                       {/* Management Buttons - only for editors/owners */}
                                       {canManageParticipants && (
                                          <>
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEditRole(participant)}
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   ml: 1,
                                                   borderColor: 'secondary.main',
                                                   color: 'secondary.main',
                                                   '&:hover': {
                                                      bgcolor: 'secondary.main',
                                                      color: 'white',
                                                      borderColor: 'secondary.main',
                                                   },
                                                }}
                                             >
                                                Edit
                                             </Button>
                                             <Button
                                                variant='outlined'
                                                size='small'
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleRemoveUser(participant)}
                                                sx={{
                                                   fontSize: '0.75rem',
                                                   py: 0.5,
                                                   px: 1.5,
                                                   ml: 1,
                                                   borderColor: 'error.main',
                                                   color: 'error.main',
                                                   '&:hover': {
                                                      bgcolor: 'error.main',
                                                      color: 'white',
                                                      borderColor: 'error.main',
                                                   },
                                                }}
                                             >
                                                Remove
                                             </Button>
                                          </>
                                       )}
                                    </Box>
                                 </ListItemSecondaryAction>
                              )}
                           </ListItem>
                        ))}
                  </List>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           mt: 3,
                           mb: 2,
                        }}
                     >
                        <Pagination
                           count={totalPages}
                           page={currentPage}
                           onChange={(_, page) => setCurrentPage(page)}
                           color='primary'
                           size='medium'
                           showFirstButton
                           showLastButton
                           sx={{
                              '& .MuiPaginationItem-root': {
                                 borderRadius: 2,
                              },
                           }}
                        />
                     </Box>
                  )}
               </Paper>
            </Box>
         )}

         {/* Delete confirmation dialog */}
         <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Remove participant?</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to remove{' '}
                  {participantToDelete?.displayName || participantToDelete?.email} from this trip?
                  This will also remove any photos they have added to the trip.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
               <Button onClick={confirmRemoveUser} color='error' variant='contained'>
                  Remove
               </Button>
            </DialogActions>
         </Dialog>

         {/* Edit role dialog */}
         <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
            <DialogTitle>Change participant role</DialogTitle>
            <DialogContent>
               <DialogContentText sx={{ mb: 2 }}>
                  Change the role for {participantToEdit?.displayName || participantToEdit?.email}
               </DialogContentText>
               <FormControl fullWidth>
                  <InputLabel id='edit-role-label'>Role</InputLabel>
                  <Select
                     labelId='edit-role-label'
                     value={newRole}
                     label='Role'
                     onChange={(e) => setNewRole(e.target.value as TripRole)}
                  >
                     <MenuItem value='viewer'>Viewer </MenuItem>
                     <MenuItem value='editor'>Editor </MenuItem>
                  </Select>
               </FormControl>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
               <Button onClick={confirmEditRole} color='primary' variant='contained'>
                  Save Changes
               </Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default TripParticipantsManager; 